import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-text-style/color';
import { FontFamily } from '@tiptap/extension-text-style/font-family';
import { FontSize } from '@tiptap/extension-text-style/font-size';
import { TableKit, TableCell, TableHeader } from '@tiptap/extension-table';
import { emptyMemoBodyHtml, normalizeMemoBodyHtml } from '../../lib/memoBody';
import { readSignatureImageDataUrl } from '../../lib/ticketEmailSignature';

function parsePx(value: string | null): number | null {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const EmailImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          parsePx(element.getAttribute('width')) ?? parsePx(element.style.width),
        renderHTML: (attributes: { width?: number | null }) => {
          if (!attributes.width) return {};
          return {
            width: String(attributes.width),
            style: `width:${attributes.width}px;height:auto;border:0;display:inline-block;`,
          };
        },
      },
      height: {
        default: null,
        parseHTML: () => null,
        renderHTML: () => ({}),
      },
    };
  },
  addNodeView() {
    const parent = this.parent?.();
    if (!parent) return null;
    return (props) => {
      const view = parent(props);
      if (!view?.dom) return view;

      // TipTap hides the node until img.onload — base64 / cached images often
      // never fire that handler, leaving pointer-events:none forever.
      const reveal = () => {
        const dom = view.dom as HTMLElement;
        dom.style.visibility = '';
        dom.style.pointerEvents = '';
      };
      const img =
        view.dom instanceof HTMLImageElement
          ? view.dom
          : view.dom.querySelector('img');
      if (img instanceof HTMLImageElement) {
        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
        }
      }
      // Always reveal on next frame as a safety net.
      requestAnimationFrame(reveal);
      window.setTimeout(reveal, 0);
      return view;
    };
  },
});

const IMAGE_WIDTHS = [48, 64, 80, 96, 120, 150, 180, 220, 280, 360] as const;

type CellVAlign = 'top' | 'middle' | 'bottom';
type CellBorder = 'none' | 'all' | 'bottom' | 'top' | 'left' | 'right';

function parseCellVAlign(element: HTMLElement): CellVAlign {
  const v = (element.style.verticalAlign || element.getAttribute('valign') || '').trim().toLowerCase();
  if (v === 'center') return 'middle';
  if (v === 'top' || v === 'middle' || v === 'bottom') return v;
  return 'middle';
}

function parseCellBorder(element: HTMLElement): CellBorder {
  const data = (element.getAttribute('data-border') || '').toLowerCase();
  if (data === 'all' || data === 'bottom' || data === 'top' || data === 'left' || data === 'right') {
    return data;
  }
  const all = (element.style.border || '').toLowerCase();
  const bottom = (element.style.borderBottom || '').toLowerCase();
  const top = (element.style.borderTop || '').toLowerCase();
  const left = (element.style.borderLeft || '').toLowerCase();
  const right = (element.style.borderRight || '').toLowerCase();
  const isSolid = (v: string) => v.includes('solid') && !v.includes('0px') && !v.includes('none');
  if (element.getAttribute('border') === '1' || isSolid(all)) return 'all';
  if (isSolid(bottom)) return 'bottom';
  if (isSolid(top)) return 'top';
  if (isSolid(left)) return 'left';
  if (isSolid(right)) return 'right';
  return 'none';
}

function cellBorderCss(kind: CellBorder): string {
  switch (kind) {
    case 'all':
      return 'border:1px solid #333333';
    case 'bottom':
      return 'border:0;border-bottom:1px solid #333333';
    case 'top':
      return 'border:0;border-top:1px solid #333333';
    case 'left':
      return 'border:0;border-left:1px solid #333333';
    case 'right':
      return 'border:0;border-right:1px solid #333333';
    default:
      return 'border:0';
  }
}

function emailCellAttributes() {
  return {
    vAlign: {
      default: 'middle' as CellVAlign,
      parseHTML: parseCellVAlign,
      renderHTML: () => ({}),
    },
    cellBorder: {
      default: 'none' as CellBorder,
      parseHTML: parseCellBorder,
      renderHTML: () => ({}),
    },
  };
}

function renderEmailCellAttrs(
  tag: 'td' | 'th',
  node: { attrs: Record<string, unknown> },
  HTMLAttributes: Record<string, unknown>,
  extra: Record<string, unknown>,
) {
  const vAlign = (String(node.attrs.vAlign || 'middle') as CellVAlign) || 'middle';
  const rawBorder = node.attrs.cellBorder;
  const cellBorder: CellBorder =
    rawBorder === true || rawBorder === 'all'
      ? 'all'
      : rawBorder === 'bottom' ||
          rawBorder === 'top' ||
          rawBorder === 'left' ||
          rawBorder === 'right'
        ? rawBorder
        : 'none';
  const align = node.attrs.align ? String(node.attrs.align) : '';
  const styles = ['padding:4px 8px', `vertical-align:${vAlign}`, cellBorderCss(cellBorder)];
  if (align) styles.push(`text-align:${align}`);
  return [
    tag,
    mergeAttributes(extra, HTMLAttributes, {
      style: styles.join(';'),
      valign: vAlign,
      ...(cellBorder !== 'none' ? { 'data-border': cellBorder } : {}),
    }),
    0,
  ] as const;
}

const EmailTableCell = TableCell.extend({
  addAttributes() {
    const parent = (this.parent?.() ?? {}) as Record<string, unknown>;
    const parentAlign = (parent.align ?? {}) as Record<string, unknown>;
    return {
      ...parent,
      align: {
        ...parentAlign,
        renderHTML: () => ({}),
      },
      ...emailCellAttributes(),
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    return renderEmailCellAttrs('td', node, HTMLAttributes, this.options.HTMLAttributes);
  },
});

const EmailTableHeader = TableHeader.extend({
  addAttributes() {
    const parent = (this.parent?.() ?? {}) as Record<string, unknown>;
    const parentAlign = (parent.align ?? {}) as Record<string, unknown>;
    return {
      ...parent,
      align: {
        ...parentAlign,
        renderHTML: () => ({}),
      },
      ...emailCellAttributes(),
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    return renderEmailCellAttrs('th', node, HTMLAttributes, this.options.HTMLAttributes);
  },
});

type Props = {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Enable logo / inline images (base64 HTML) for email signatures. */
  allowImages?: boolean;
  /** Outlook-style font family, size, colour, and alignment. */
  emailFormat?: boolean;
  /** Start the editor with Calibri 11pt (Outlook reply default). */
  defaultEmailFont?: boolean;
};

const MEMO_FONT_SIZES = [
  { label: 'Small', value: '10pt' },
  { label: 'Normal', value: '11pt' },
  { label: 'Large', value: '14pt' },
] as const;

const EMAIL_FONT_SIZES = [
  { label: '8', value: '8pt' },
  { label: '9', value: '9pt' },
  { label: '10', value: '10pt' },
  { label: '11', value: '11pt' },
  { label: '12', value: '12pt' },
  { label: '14', value: '14pt' },
  { label: '16', value: '16pt' },
  { label: '18', value: '18pt' },
  { label: '20', value: '20pt' },
  { label: '24', value: '24pt' },
  { label: '28', value: '28pt' },
  { label: '36', value: '36pt' },
] as const;

const EMAIL_DEFAULT_FONT = "Calibri, 'Segoe UI', sans-serif";
const EMAIL_DEFAULT_FONT_STYLE =
  "margin:0;line-height:1.15;font-family:Calibri,'Segoe UI',sans-serif;font-size:11pt;";

const EMAIL_FONTS = [
  { label: 'Calibri', value: "Calibri, 'Segoe UI', sans-serif" },
  { label: 'Segoe UI', value: "'Segoe UI', Tahoma, sans-serif" },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Courier New', value: "'Courier New', Courier, monospace" },
  { label: 'Comic Sans', value: "'Comic Sans MS', 'Segoe Print', cursive" },
  { label: 'Segoe Script', value: "'Segoe Script', 'Brush Script MT', cursive" },
  { label: 'Brush Script', value: "'Brush Script MT', 'Segoe Script', cursive" },
  { label: 'Lucida Handwriting', value: "'Lucida Handwriting', 'Segoe Script', cursive" },
] as const;

const EMAIL_COLORS = [
  '#000000',
  '#333333',
  '#9b1c1c',
  '#c2410c',
  '#ca8a04',
  '#15803d',
  '#1d4ed8',
  '#6d28d9',
  '#be185d',
  '#64748b',
];

function normalizeLinkHref(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (/^(https?:\/\/|mailto:|tel:)/i.test(value)) return value;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
  if (/^www\./i.test(value)) return `https://${value}`;
  return value;
}

function toColorInputValue(value?: string): string {
  const raw = (value ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const hex = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${hex(rgb[1]!)}${hex(rgb[2]!)}${hex(rgb[3]!)}`;
  }
  return '#333333';
}

export function MemoBodyEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Kindly state the purpose of this memorandum and any required action.',
  allowImages = false,
  emailFormat = false,
  defaultEmailFont = false,
}: Props) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const lastEmittedHtml = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [imageError, setImageError] = useState<string | null>(null);
  const [, setSelTick] = useState(0);
  const fontSizes = emailFormat ? EMAIL_FONT_SIZES : MEMO_FONT_SIZES;

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: emailFormat ? undefined : false,
        horizontalRule: emailFormat ? undefined : false,
        orderedList: emailFormat ? undefined : false,
        underline: false,
        paragraph: emailFormat
          ? {
              HTMLAttributes: {
                style: EMAIL_DEFAULT_FONT_STYLE,
              },
            }
          : undefined,
      }),
      Underline,
      TextStyle,
      FontSize,
      ...(emailFormat
        ? [
            FontFamily,
            Color,
            TextAlign.configure({ types: ['paragraph'] }),
            TableKit.configure({
              table: {
                resizable: true,
                HTMLAttributes: {
                  style: 'border-collapse:collapse;width:auto;',
                },
              },
              tableCell: false,
              tableHeader: false,
            }),
            EmailTableCell,
            EmailTableHeader,
          ]
        : []),
      Highlight.configure({ multicolor: false }),
      Placeholder.configure({ placeholder }),
      ...(emailFormat
        ? [
            Link.configure({
              openOnClick: false,
              autolink: true,
              defaultProtocol: 'https',
              protocols: ['http', 'https', 'mailto', 'tel'],
              HTMLAttributes: {
                rel: 'noopener noreferrer',
              },
            }),
          ]
        : []),
      ...(allowImages
        ? [
            EmailImage.configure({
              inline: true,
              allowBase64: true,
              resize: {
                enabled: true,
                directions: ['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'],
                minWidth: 40,
                minHeight: 40,
                alwaysPreserveAspectRatio: true,
              },
              HTMLAttributes: {
                class: 'memo-body-editor__img',
              },
            }),
          ]
        : []),
    ],
    [allowImages, defaultEmailFont, emailFormat, placeholder],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: normalizeMemoBodyHtml(value) || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const next = emptyMemoBodyHtml(html) ? '' : html;
      lastEmittedHtml.current = next;
      onChangeRef.current(next);
    },
    onSelectionUpdate: () => {
      setSelTick((n) => n + 1);
    },
    editorProps: {
      attributes: {
        class: emailFormat
          ? 'memo-body-editor__content memo-body-editor__content--email'
          : 'memo-body-editor__content',
        spellcheck: 'true',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedHtml.current) return;
    if (editor.isFocused) return;
    const next = normalizeMemoBodyHtml(value) || '';
    const current = emptyMemoBodyHtml(editor.getHTML()) ? '' : editor.getHTML();
    if (next === current) {
      lastEmittedHtml.current = value;
      return;
    }
    editor.commands.setContent(next, { emitUpdate: false });
    lastEmittedHtml.current = next;
  }, [editor, value]);

  async function insertImageFile(file: File | undefined) {
    if (!file || !editor) return;
    setImageError(null);
    try {
      const src = await readSignatureImageDataUrl(file);
      editor.chain().focus().setImage({ src, alt: 'Inserted image', width: 220 }).run();
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'Could not insert image.');
    }
  }

  function setSelectedImageWidth(width: number) {
    if (!editor?.isActive('image')) return;
    editor.chain().focus().updateAttributes('image', { width, height: null }).run();
    const selected = editor.view.dom.querySelector(
      'img.ProseMirror-selectednode, .ProseMirror-selectednode img',
    ) as HTMLImageElement | null;
    if (selected) {
      selected.style.width = `${width}px`;
      selected.style.height = 'auto';
    }
  }

  function applyLink() {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selected = editor.state.doc.textBetween(from, to, ' ').trim();
    const current = String(editor.getAttributes('link').href ?? '');
    const next = window.prompt(
      'Link URL. For email use the address or mailto:name@company.com. Leave blank to remove the link.',
      current || selected,
    );
    if (next === null) return;
    const href = normalizeLinkHref(next);
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }

  function applyTextAlign(align: 'left' | 'center' | 'right') {
    if (!editor) return;
    const chain = editor.chain().focus().setTextAlign(align);
    if (editor.isActive('table')) {
      chain.setCellAttribute('align', align);
    }
    chain.run();
  }

  function applyCellVAlign(vAlign: CellVAlign) {
    if (!editor) return;
    editor.chain().focus().setCellAttribute('vAlign', vAlign).run();
  }

  function applyCellBorder(kind: CellBorder) {
    if (!editor) return;
    editor.chain().focus().setCellAttribute('cellBorder', kind).run();
  }

  if (!editor) {
    return (
      <div className="memo-body-editor memo-body-editor--loading">
        <div className="memo-body-editor__surface" />
      </div>
    );
  }

  const styleAttrs = editor.getAttributes('textStyle') as {
    fontSize?: string;
    fontFamily?: string;
    color?: string;
  };
  const activeSize = styleAttrs.fontSize || (defaultEmailFont ? '11pt' : '');
  const activeFont = styleAttrs.fontFamily || (defaultEmailFont ? EMAIL_DEFAULT_FONT : '');
  const activeColor = toColorInputValue(styleAttrs.color);
  const matchedFont = EMAIL_FONTS.find((f) => f.value === activeFont)?.value ?? '';
  const imageWidth = Number(editor.getAttributes('image').width) || 0;
  const cellVAlign = (editor.getAttributes('tableCell').vAlign ||
    editor.getAttributes('tableHeader').vAlign ||
    'middle') as CellVAlign;
  const rawCellBorder =
    editor.getAttributes('tableCell').cellBorder ?? editor.getAttributes('tableHeader').cellBorder;
  const cellBorder: CellBorder =
    rawCellBorder === true || rawCellBorder === 'all'
      ? 'all'
      : rawCellBorder === 'bottom' ||
          rawCellBorder === 'top' ||
          rawCellBorder === 'left' ||
          rawCellBorder === 'right'
        ? rawCellBorder
        : 'none';

  return (
    <div
      className={`memo-body-editor${disabled ? ' is-readonly' : ''}${
        emailFormat ? ' memo-body-editor--email' : ''
      }`}
    >
      {!disabled ? (
        <div className="memo-body-editor__toolbar" role="toolbar" aria-label="Body formatting">
          {emailFormat ? (
            <>
              <label className="memo-body-editor__size memo-body-editor__font">
                <span className="sr-only">Font</span>
                <select
                  className="memo-body-editor__select memo-body-editor__select--font"
                  value={matchedFont}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      editor.chain().focus().unsetFontFamily().run();
                      return;
                    }
                    editor.chain().focus().setFontFamily(v).run();
                  }}
                >
                  <option value="">Font</option>
                  {EMAIL_FONTS.map((f) => (
                    <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <label className="memo-body-editor__size">
            <span className="sr-only">Font size</span>
            <select
              className="memo-body-editor__select"
              value={fontSizes.some((s) => s.value === activeSize) ? activeSize : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  editor.chain().focus().unsetFontSize().run();
                  return;
                }
                editor.chain().focus().setFontSize(v).run();
              }}
            >
              <option value="">Size</option>
              {fontSizes.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <span className="memo-body-editor__sep" aria-hidden />
          <button
            type="button"
            className={`memo-body-editor__btn${editor.isActive('bold') ? ' is-active' : ''}`}
            title="Bold"
            aria-pressed={editor.isActive('bold')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className={`memo-body-editor__btn${editor.isActive('italic') ? ' is-active' : ''}`}
            title="Italic"
            aria-pressed={editor.isActive('italic')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className={`memo-body-editor__btn${editor.isActive('underline') ? ' is-active' : ''}`}
            title="Underline"
            aria-pressed={editor.isActive('underline')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <span className="memo-body-editor__u">U</span>
          </button>
          {emailFormat ? (
            <button
              type="button"
              className={`memo-body-editor__btn${editor.isActive('strike') ? ' is-active' : ''}`}
              title="Strikethrough"
              aria-pressed={editor.isActive('strike')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <span className="memo-body-editor__strike">S</span>
            </button>
          ) : null}
          {emailFormat ? (
            <>
              <label className="memo-body-editor__color" title="Font colour">
                <span className="sr-only">Font colour</span>
                <input
                  type="color"
                  className="memo-body-editor__color-input"
                  value={activeColor}
                  onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                />
              </label>
              <span className="memo-body-editor__swatches" aria-label="Colour presets">
                {EMAIL_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`memo-body-editor__swatch${activeColor === c ? ' is-active' : ''}`}
                    style={{ background: c }}
                    title={c}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().setColor(c).run()}
                  />
                ))}
              </span>
            </>
          ) : null}
          <button
            type="button"
            className={`memo-body-editor__btn memo-body-editor__btn--highlight${
              editor.isActive('highlight') ? ' is-active' : ''
            }`}
            title="Highlight"
            aria-pressed={editor.isActive('highlight')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          >
            HL
          </button>
          {emailFormat ? (
            <button
              type="button"
              className={`memo-body-editor__btn${editor.isActive('link') ? ' is-active' : ''}`}
              title="Insert or edit hyperlink"
              aria-pressed={editor.isActive('link')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyLink()}
            >
              Link
            </button>
          ) : null}
          {emailFormat ? (
            <>
              <span className="memo-body-editor__sep" aria-hidden />
              <button
                type="button"
                className={`memo-body-editor__btn${editor.isActive({ textAlign: 'left' }) ? ' is-active' : ''}`}
                title="Align left"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyTextAlign('left')}
              >
                L
              </button>
              <button
                type="button"
                className={`memo-body-editor__btn${editor.isActive({ textAlign: 'center' }) ? ' is-active' : ''}`}
                title="Align center"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyTextAlign('center')}
              >
                C
              </button>
              <button
                type="button"
                className={`memo-body-editor__btn${editor.isActive({ textAlign: 'right' }) ? ' is-active' : ''}`}
                title="Align right"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyTextAlign('right')}
              >
                R
              </button>
            </>
          ) : null}
          <span className="memo-body-editor__sep" aria-hidden />
          <button
            type="button"
            className={`memo-body-editor__btn${editor.isActive('bulletList') ? ' is-active' : ''}`}
            title="Bullet list"
            aria-pressed={editor.isActive('bulletList')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            • List
          </button>
          {emailFormat ? (
            <button
              type="button"
              className={`memo-body-editor__btn${editor.isActive('orderedList') ? ' is-active' : ''}`}
              title="Numbered list"
              aria-pressed={editor.isActive('orderedList')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              1.
            </button>
          ) : null}
          {emailFormat ? (
            <>
              <span className="memo-body-editor__sep" aria-hidden />
              <button
                type="button"
                className={`memo-body-editor__btn${editor.isActive('table') ? ' is-active' : ''}`}
                title="Insert table (2×2)"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: false }).run()
                }
              >
                Table
              </button>
              {editor.isActive('table') ? (
                <>
                  <button
                    type="button"
                    className="memo-body-editor__btn"
                    title="Add row"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                  >
                    +Row
                  </button>
                  <button
                    type="button"
                    className="memo-body-editor__btn"
                    title="Add column"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                  >
                    +Col
                  </button>
                  <button
                    type="button"
                    className="memo-body-editor__btn"
                    title="Delete row"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().deleteRow().run()}
                  >
                    −Row
                  </button>
                  <button
                    type="button"
                    className="memo-body-editor__btn"
                    title="Delete column"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                  >
                    −Col
                  </button>
                  <label className="memo-body-editor__size" title="Border for this cell only">
                    <span className="sr-only">Cell border</span>
                    <select
                      className="memo-body-editor__select"
                      value={cellBorder}
                      onChange={(e) => applyCellBorder(e.target.value as CellBorder)}
                    >
                      <option value="none">No line</option>
                      <option value="all">All sides</option>
                      <option value="bottom">Bottom</option>
                      <option value="top">Top</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className={`memo-body-editor__btn${cellVAlign === 'top' ? ' is-active' : ''}`}
                    title="Align cell content to top"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyCellVAlign('top')}
                  >
                    Top
                  </button>
                  <button
                    type="button"
                    className={`memo-body-editor__btn${cellVAlign === 'middle' ? ' is-active' : ''}`}
                    title="Center cell content vertically"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyCellVAlign('middle')}
                  >
                    Mid
                  </button>
                  <button
                    type="button"
                    className={`memo-body-editor__btn${cellVAlign === 'bottom' ? ' is-active' : ''}`}
                    title="Align cell content to bottom"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyCellVAlign('bottom')}
                  >
                    Bot
                  </button>
                  <button
                    type="button"
                    className="memo-body-editor__btn"
                    title="Delete table"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().deleteTable().run()}
                  >
                    Del table
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          {allowImages ? (
            <>
              <span className="memo-body-editor__sep" aria-hidden />
              <button
                type="button"
                className="memo-body-editor__btn"
                title="Insert image (logo)"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => imageInputRef.current?.click()}
              >
                Image
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  void insertImageFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <label className="memo-body-editor__size">
                <span className="sr-only">Image width</span>
                <select
                  className="memo-body-editor__select"
                  disabled={!editor.isActive('image')}
                  value={IMAGE_WIDTHS.some((w) => w === imageWidth) ? String(imageWidth) : ''}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v) setSelectedImageWidth(v);
                  }}
                >
                  <option value="">
                    {editor.isActive('image')
                      ? imageWidth
                        ? `${imageWidth}px`
                        : 'Width'
                      : 'Select image'}
                  </option>
                  {IMAGE_WIDTHS.map((w) => (
                    <option key={w} value={w}>
                      {w}px
                    </option>
                  ))}
                </select>
              </label>
              {editor.isActive('image') ? (
                <button
                  type="button"
                  className="memo-body-editor__btn"
                  title="Remove image"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => editor.chain().focus().deleteSelection().run()}
                >
                  Del img
                </button>
              ) : (
                <span className="memo-body-editor__hint" title="Click the image, then drag the red handles or pick a width">
                  Drag corners to resize
                </span>
              )}
            </>
          ) : null}
        </div>
      ) : null}
      {imageError && allowImages ? (
        <p className="memo-body-editor__image-error form-error">{imageError}</p>
      ) : null}
      <div
        className="memo-body-editor__surface"
        onMouseDown={(e) => {
          if (disabled) return;
          const target = e.target as HTMLElement;
          if (target.closest('.ProseMirror')) return;
          e.preventDefault();
          editor.chain().focus('end').run();
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
